// ============================================================================
// dataTypes/liveStreamManagment/sourceRegistry/acquirers/DeviceAcquirer.ts
// Acquiert un flux webcam locale via getUserMedia.
//
// Le SourceRegistry garantit qu'on n'ouvre jamais deux fois le même deviceId.
// Si une scène a besoin de la même caméra, elle utilise borrow() qui
// partage le stream existant sans ré-appeler getUserMedia.
// ============================================================================

import { Source, SourceAcquirer, DeviceSourceConfig } from '../types';
import { DEFAULT_CANVAS_CONFIG } from '../../canvasPipeline/types';

export class DeviceAcquirer implements SourceAcquirer {

  private readonly _cleanups = new Map<string, () => void>();

  /**
   * Appelé quand la piste vidéo d'une source acquise s'arrête d'elle-même.
   * Câblé sur `registry.reportFailure` dans la factory — même motif que le
   * `onEnded` des acquirers jingle et sound, à ceci près qu'une piste caméra qui
   * s'arrête est toujours une panne, jamais une fin normale.
   */
  onTrackEnded?: (sourceId: string, reason: string) => void;

  async acquire(source: Source): Promise<MediaStream> {
    const config = source.config as DeviceSourceConfig;

    const constraints: MediaStreamConstraints = {
      video: {
        deviceId:  config.deviceId ? { exact: config.deviceId } : undefined,
        width:     { ideal: config.video?.width     ?? 1920 },
        height:    { ideal: config.video?.height    ?? 1080 },
        // La chaîne entière travaille à 25 images par seconde : le canvas de
        // composition (`DEFAULT_CANVAS_CONFIG.targetFps`), la scène, et la sortie
        // WHIP qui s'y aligne. Demander 30 à la caméra faisait capturer, décoder et
        // dessiner cinq images par seconde que le canvas jetait ensuite — du travail
        // pur perdu, et un argument de plus donné au navigateur pour préférer une
        // définition moindre lorsqu'il ne peut pas tenir les deux.
        //
        // Une seule source de vérité, donc, et c'est celle du canvas.
        frameRate: { ideal: config.video?.frameRate ?? DEFAULT_CANVAS_CONFIG.targetFps },
        facingMode: config.video?.facingMode
          ? { ideal: config.video.facingMode }
          : undefined,
      },
      // Pas d'audio ici — l'audio est géré par l'AudioMixerPipeline
      // qui ouvre son propre getUserMedia audio si nécessaire.
      audio: false,
    };

    // `ideal` seul laisse le navigateur arbitrer, et il arbitre souvent CONTRE la
    // définition : une C920 sait faire du 1080p, mais pas en 30 images par seconde
    // dans un format non compressé — Chrome retombe alors sur du 720p, voire moins,
    // pour tenir la cadence demandée. Résultat mesuré le 25/08/2026 : un angle
    // d'enregistrement en 640×360.
    //
    // `advanced` exprime une préférence FORTE et sans risque : le navigateur essaie
    // de la satisfaire entièrement, et l'ignore purement et simplement s'il ne peut
    // pas. Une caméra qui ne sait pas faire du 1080p continue donc de s'ouvrir,
    // comme avant — mais celle qui sait le fera.
    if (!config.video?.width && !config.video?.height) {
      (constraints.video as MediaTrackConstraints).advanced = [
        { width: 1920, height: 1080 },
        { width: 1280, height: 720  },
      ];
    }

    // ── Réessai sur périphérique encore occupé ────────────────────────────────
    //
    // Le registre des sources est détruit et recréé à CHAQUE changement de chaîne :
    // son provider vit dans le sous-arbre que `BroadcastingChannelList` remonte par
    // sa `key`. La destruction arrête réellement les pistes (`destroy` → `forceRelease`
    // → `track.stop()`), et la chaîne suivante rouvre la caméra dans la foulée.
    //
    // Or un périphérique tout juste relâché n'est pas immédiatement réouvrable : le
    // système a besoin d'un instant, et `getUserMedia` répond alors `NotReadableError`
    // (« Could not start video source ») ou `AbortError`. L'échec n'était rattrapé nulle
    // part — la source passait en erreur et, par conception, RIEN ne réessaie : « la
    // relance est manuelle, depuis la fenêtre de réglage du slot ». D'où la caméra
    // locale absente après une bascule de chaîne, sans un mot à l'écran.
    //
    // Deux nouvelles tentatives espacées suffisent à couvrir cette course. Les autres
    // refus — permission refusée, périphérique disparu, contrainte impossible — sont
    // définitifs : on les relaie tels quels, sans attendre pour rien.
    const TRANSIENT = new Set(['NotReadableError', 'AbortError']);
    const DELAIS_MS = [0, 300, 700];

    // Ouverture PHYSIQUE de la caméra — appelée seulement quand le pool n'a pas déjà une piste
    // maîtresse vivante pour ces contraintes (cf. `obtenirEntree`).
    const ouvrirMaitre = async (): Promise<MediaStream> => {
    let stream: MediaStream | null = null;

    for (let essai = 0; essai < DELAIS_MS.length; essai++) {
      if (DELAIS_MS[essai] > 0) await new Promise(r => setTimeout(r, DELAIS_MS[essai]));
      try {
        const candidat = await navigator.mediaDevices.getUserMedia(constraints);
        // `getUserMedia` peut rendre un succès NÉ MORT : la piste vidéo a déjà
        // `readyState === 'ended'` dès la réponse, sans qu'aucune exception n'ait été
        // levée et sans que l'évènement 'ended' (écouté plus bas) ne se déclenche jamais
        // — le navigateur n'a rien à notifier puisqu'à ses yeux la piste n'a jamais été
        // vivante. Constaté le 15/09/2026 : la webcam restait noire après un rechargement
        // complet de la page, donc SANS aucun état partagé d'une chaîne précédente en
        // cause — c'est bien l'ouverture elle-même qui peut échouer de cette façon,
        // vraisemblablement quand le périphérique vient tout juste d'être libéré ailleurs.
        // Même remède que le cas NotReadableError/AbortError ci-dessus : une nouvelle
        // tentative, sans quoi rien ne le signale jamais et le flux mort reste assigné.
        const videoTrack = candidat.getVideoTracks()[0];
        if (videoTrack && videoTrack.readyState !== 'live') {
          candidat.getTracks().forEach(t => t.stop());
          if (essai === DELAIS_MS.length - 1) {
            throw new Error(`[DeviceAcquirer] ${source.id} : piste vidéo déjà terminée dès l'ouverture, après ${DELAIS_MS.length} tentatives`);
          }
          console.warn(`[DeviceAcquirer] ${source.id} ouverte mais déjà morte (readyState=${videoTrack.readyState}) — nouvelle tentative dans ${DELAIS_MS[essai + 1]} ms`);
          continue;
        }
        stream = candidat;
        if (essai > 0) console.warn(`[DeviceAcquirer] ${source.id} ouverte à la tentative ${essai + 1}`);
        break;
      } catch (err) {
        const nom = (err as any)?.name ?? '';
        if (!TRANSIENT.has(nom) || essai === DELAIS_MS.length - 1) {
          if (TRANSIENT.has(nom))
            console.warn(`[DeviceAcquirer] ${source.id} toujours occupée après ${DELAIS_MS.length} tentatives (${nom})`);
          throw err;
        }
        console.warn(`[DeviceAcquirer] ${source.id} occupée (${nom}) — nouvelle tentative dans ${DELAIS_MS[essai + 1]} ms`);
      }
    }

    if (!stream) throw new Error(`[DeviceAcquirer] Ouverture impossible pour ${source.id}`);
    return stream;
    };

    // ── Pool de caméras, partagé entre les chaînes ────────────────────────────
    //
    // Le registre des sources est détruit et recréé à chaque changement de chaîne, et chaque
    // destruction coupait la caméra pour que la suivante la rouvre : réouverture lente, périphérique
    // « tout juste relâché » refusé, et courses avec les relâchements des mini-vues (constaté le
    // 20/09/2026 : la webcam était perdue en changeant de chaîne, C920 comme OBS Virtual Camera).
    //
    // Désormais la caméra n'est ouverte qu'UNE fois, par la première chaîne qui en a besoin : le
    // pool garde une piste MAÎTRESSE, et chaque registre reçoit un flux fait d'un CLONE de cette
    // piste. Couper un clone (destruction du registre, effets vidéo qui remplacent la piste) ne
    // touche ni la maîtresse ni les clones des autres. La chaîne suivante n'a plus qu'à cloner ;
    // si la caméra manque (débranchée, pas encore ouverte), elle est ouverte à ce moment-là.
    // Sans emprunteur, la maîtresse est coupée après un délai de grâce — assez pour couvrir un
    // changement de chaîne, assez court pour que le voyant de la caméra s'éteigne en quittant la régie.
    const cle    = JSON.stringify(constraints.video);
    this.abandonnerBail(source.id);              // bail périmé d'une acquisition jamais relâchée
    const entree = await DeviceAcquirer.obtenirEntree(cle, ouvrirMaitre);
    const piste  = entree.maitre.getVideoTracks()[0];
    if (!piste) throw new Error(`[DeviceAcquirer] ${source.id} : piste maîtresse absente`);
    entree.baux++;
    if (entree.grace) { clearTimeout(entree.grace); entree.grace = null; }
    this._baux.set(source.id, { cle, entree });
    const stream = new MediaStream([piste.clone()]);

    // Surveillance : si la track s'arrête (user révoque la permission,
    // débranchement USB, caméra confisquée par une autre application, sortie de
    // veille…), la source doit passer en erreur. Jusqu'au 22/08/2026 ce handler
    // se contentait d'un console.warn : le registre continuait de croire la
    // source `active`, et le deck affichait un emplacement pourvu dont la
    // mini-vue restait noire, sans que rien n'explique pourquoi.
    const videoTrack = stream.getVideoTracks()[0];
    if (videoTrack) {
      const onEnded = () => {
        const reason = `Piste vidéo interrompue (${videoTrack.label || 'périphérique inconnu'})`;
        console.warn(`[DeviceAcquirer] Track ended pour ${source.id} — ${reason}`);
        this.onTrackEnded?.(source.id, reason);
      };
      videoTrack.addEventListener('ended', onEnded);
      this._cleanups.set(source.id, () => videoTrack.removeEventListener('ended', onEnded));
    }

    return stream;
  }

  release(source: Source): void {
    this._cleanups.get(source.id)?.();
    this._cleanups.delete(source.id);
    if (source.stream) {
      // Diagnostic (20/09/2026) : un `track.stop()` ne déclenche PAS l'évènement 'ended', donc une
      // piste arrêtée par NOTRE code ne laisse aucune trace — c'est ce qu'on a vu entre 16h38 et
      // 16h41 (flux webcam mort à l'emprunt, sans « Track ended » ni « déjà morte ») sans pouvoir
      // dire qui l'avait arrêtée. On note l'appelant, seulement quand une piste vivante est
      // réellement coupée.
      if (source.stream.getTracks().some(t => t.readyState === 'live')) {
        const appelants = (new Error().stack ?? '').split('\n').slice(2, 7).map(l => l.trim().replace(/^at /, '')).join(' ← ');
        console.warn(`[DeviceAcquirer] arrêt des pistes de ${source.id} (refCount=${source.refCount}) — ${appelants}`);
      }
      source.stream.getTracks().forEach(track => track.stop());
    }
    this.abandonnerBail(source.id);
  }

  // ── Pool (voir `acquire`) ────────────────────────────────────────────────────

  /** Rend le bail de cette source au pool ; la maîtresse n'est coupée qu'après le délai de grâce. */
  private abandonnerBail(sourceId: string): void {
    const bail = this._baux.get(sourceId);
    if (!bail) return;
    this._baux.delete(sourceId);
    const { cle, entree } = bail;
    entree.baux = Math.max(0, entree.baux - 1);
    if (entree.baux === 0 && !entree.grace) {
      entree.grace = setTimeout(() => {
        entree.grace = null;
        if (entree.baux === 0) DeviceAcquirer.arreterEntree(cle, entree);
      }, DELAI_GRACE_MS);
    }
  }

  private static arreterEntree(cle: string, entree: EntreePool): void {
    if (entree.grace) { clearTimeout(entree.grace); entree.grace = null; }
    entree.maitre.getTracks().forEach(t => t.stop());
    if (DeviceAcquirer.pool.get(cle) === entree) DeviceAcquirer.pool.delete(cle);
  }

  private static async obtenirEntree(cle: string, ouvrir: () => Promise<MediaStream>): Promise<EntreePool> {
    const existante = DeviceAcquirer.pool.get(cle);
    if (existante) {
      if (existante.maitre.getTracks().every(t => t.readyState === 'live')) return existante;
      DeviceAcquirer.arreterEntree(cle, existante);   // maîtresse morte : on rouvre
    }
    // Deux registres peuvent demander la même caméra en même temps (changement de chaîne) :
    // une seule ouverture, les deux attendent la même promesse.
    const enCours = DeviceAcquirer.enAttente.get(cle);
    if (enCours) return enCours;

    const promesse = (async () => {
      const maitre = await ouvrir();
      const entree: EntreePool = { maitre, baux: 0, grace: null };
      DeviceAcquirer.pool.set(cle, entree);
      // Périphérique débranché / retiré par le navigateur : le pool l'oublie, la prochaine
      // acquisition rouvrira (ou échouera proprement si la caméra a vraiment disparu).
      maitre.getVideoTracks()[0]?.addEventListener('ended', () => {
        if (DeviceAcquirer.pool.get(cle) === entree) DeviceAcquirer.pool.delete(cle);
      });
      return entree;
    })().finally(() => { DeviceAcquirer.enAttente.delete(cle); });

    DeviceAcquirer.enAttente.set(cle, promesse);
    return promesse;
  }

  private static readonly pool      = new Map<string, EntreePool>();
  private static readonly enAttente = new Map<string, Promise<EntreePool>>();
  private readonly _baux            = new Map<string, { cle: string; entree: EntreePool }>();

  /** Coupe toutes les caméras du pool (fermeture de la page). */
  static arreterTout(): void {
    DeviceAcquirer.pool.forEach((entree, cle) => DeviceAcquirer.arreterEntree(cle, entree));
  }
}

/** Piste maîtresse ouverte une fois pour toutes les chaînes, et le nombre de registres qui l'utilisent. */
interface EntreePool {
  maitre: MediaStream;
  baux:   number;
  grace:  ReturnType<typeof setTimeout> | null;
}

/** Délai avant de couper une caméra que plus aucun registre n'emprunte. */
const DELAI_GRACE_MS = 15_000;

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => DeviceAcquirer.arreterTout());
}
